import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { Alert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert';
import { ErrorCode } from '@thoughtspot/radiant-react/widgets/alert/alert-constants';
import { Button } from '@thoughtspot/radiant-react/widgets/button/button';
import { Divider } from '@thoughtspot/radiant-react/widgets/divider';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { SearchBar } from '@thoughtspot/radiant-react/widgets/search-bar';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { VariableResponseContent } from "../../services/generated/graphql-types";
import { useDeleteVariableMutation, useDeleteVariablesMutation, useSearchVariablesQuery, useUpdateVariableNameMutation, } from "../../services/variables/variables-service";
import { BasePageContext } from "../../contexts/basepage-context";
import { ListContainer } from '../user-management/list-container/list.container';
import { CreateEditModal } from './create-edit-modal/create-edit-modal.container';
import { DeleteConfirmationModal } from './delete-confirmation-modal/delete-confirmation-modal';
import { DependentsModal } from './dependents-modal/dependents-modal';
import { RenameModal } from './rename-modal/rename-modal';
import { useDataHook } from './useDataHook';
import { categorizeDeleteError, extractDebugMessage, extractErrorCode, getColumnDefinitions, getSelectedOnCurrentPage, getToastMessage, onRowsSelectAction, refreshCells, updateRowData, } from './utils';
import { COMMON_ERROR_CODE, defaultVariableParams, ListActionType, MODE, sortKeyMap, TableColumnHeaders, TranslationsKeys, VARIABLE_NAME_EXISTS_ERROR_CODE, } from './variable-interface';
import styles from './variables.module.scss';
import { trackVariableDelete, trackVariablePageVisited, trackVariableRename, } from './variables-analytics';
export const VariablesContainer = () => {
    const { t } = useTranslation();
    const { metadataList } = BasePageContext();
    const currentData = useRef(null);
    const gridRef = useRef(null);
    const latestDataRef = useRef([]);
    const { deleteVariables } = useDeleteVariablesMutation();
    const { deleteVariable } = useDeleteVariableMutation();
    const { updateVariableName } = useUpdateVariableNameMutation();
    const [rowData, setRowData] = useState([]);
    const [errorBanner, setErrorBanner] = useState(false);
    const [errorBannerMessage, setErrorBannerMessage] = useState(null);
    const [isToastAlertVisible, setIsToastAlertVisible] = useState(false);
    const [mode, setMode] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefresh, setIsRefresh] = useState(false);
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
    const [renameNameError, setRenameNameError] = useState(null);
    const [lastOperatedPage, setLastOperatedPage] = useState(0);
    const [selectedRowsIds, setSelectedRowsIds] = useState([]);
    const [showDependentsModal, setShowDependentsModal] = useState(false);
    const [dependentsInfo, setDependentsInfo] = useState({
        message: null,
        variableName: null,
    });
    const [variableListInput, setVariableListInput] = useState({
        ...defaultVariableParams,
    });
    const sortData = useRef({
        sort: defaultVariableParams.sort,
        sortAscending: defaultVariableParams.sortAscending,
    });
    const variableQueryInput = useMemo(() => ({
        record_offset: variableListInput.offset,
        record_size: variableListInput.batchSize,
        response_content: VariableResponseContent.MetadataAndValues,
        variable_details: {
            name_pattern: variableListInput.pattern || '',
        },
    }), [
        variableListInput.offset,
        variableListInput.batchSize,
        variableListInput.pattern,
    ]);
    const { data, loading, isLastBatch, refetch } = useDataHook(useSearchVariablesQuery, isRefresh, variableQueryInput, setIsLoading, setIsRefresh);
    useEffect(() => {
        trackVariablePageVisited();
    }, []);
    const hideErrorBanner = () => {
        setErrorBanner(false);
        setErrorBannerMessage(null);
        setDependentsInfo({ message: null, variableName: null });
    };
    const successBannerMode = (successMode) => {
        setMode(successMode);
        setIsToastAlertVisible(true);
        setIsRefresh(true);
        hideErrorBanner();
        refetch();
    };
    const onChangeOffset = (offset) => {
        const { sort, sortAscending } = sortData.current;
        setVariableListInput({
            ...variableListInput,
            offset,
            sort,
            sortAscending,
        });
        setIsLoading(true);
    };
    const hasData = data && data.length > 0;
    const noDataProps = {
        title: !variableListInput.pattern && !isLoading && !hasData
            ? t(TranslationsKeys.NO_VARIABLES_DESC)
            : t(TranslationsKeys.NO_MATCH),
        description: '',
        img: ErrorCode.GLOBAL_DATA,
    };
    const onSearchChange = (term) => {
        const { sortAscending } = sortData.current;
        setVariableListInput({
            ...variableListInput,
            offset: 0,
            sortAscending,
            pattern: term,
        });
        setIsLoading(true);
    };
    const onClickCreateVariableBtn = () => {
        hideErrorBanner();
        setIsModalOpened(true);
        setIsEditMode(false);
        currentData.current = null;
    };
    const openEditModal = useCallback((variable) => {
        const freshVariable = latestDataRef.current.find(v => v.id === variable.id);
        currentData.current = freshVariable || variable;
        setIsModalOpened(true);
        setIsEditMode(true);
    }, []);
    const onClickDeleteVariableBtn = useCallback(() => {
        hideErrorBanner();
        setIsDeleteModalOpen(true);
    }, []);
    const onClickEditVariableBtn = useCallback(() => {
        if (selectedRowsIds.length === 1) {
            const selectedVariable = latestDataRef.current.find(v => v.id === selectedRowsIds[0]);
            if (selectedVariable) {
                openEditModal(selectedVariable);
            }
        }
    }, [selectedRowsIds, openEditModal]);
    const onClickRenameVariableBtn = useCallback(() => {
        if (selectedRowsIds.length === 1) {
            const selectedVariable = rowData.find(v => v.id === selectedRowsIds[0]);
            if (selectedVariable) {
                currentData.current = selectedVariable;
                setIsRenameModalOpen(true);
            }
        }
    }, [selectedRowsIds, rowData]);
    const renderActionsBtn = (text, onClick, icon) => (React.createElement(Button, { text: t(text), onClick: onClick, type: "secondary", icon: icon }));
    const renderSelectionBar = () => {
        if (!selectedRowsIds.length) {
            return null;
        }
        const selectedCount = selectedRowsIds.length;
        const isSingleSelection = selectedCount === 1;
        return (React.createElement(Horizontal, { className: styles.selectionBar },
            React.createElement(Typography, { variant: "p" }, selectedCount === 1
                ? t(TranslationsKeys.SELECTED_COUNT_SINGLE)
                : t(TranslationsKeys.SELECTED_COUNT_MULTIPLE, {
                    count: selectedCount,
                })),
            React.createElement(Divider, { vertical: true, className: styles.selectionDivider }),
            isSingleSelection ? (React.createElement(Horizontal, { spacing: "c" },
                React.createElement(Button, { text: t(TranslationsKeys.ACTION_EDIT), onClick: onClickEditVariableBtn, type: "secondary" }),
                React.createElement(Button, { text: t(TranslationsKeys.ACTION_RENAME), onClick: onClickRenameVariableBtn, type: "secondary" }),
                React.createElement(Button, { text: t(TranslationsKeys.ACTION_DELETE), onClick: onClickDeleteVariableBtn, type: "secondary" }))) : (React.createElement(Button, { text: t(TranslationsKeys.ACTION_DELETE), onClick: onClickDeleteVariableBtn, type: "secondary" }))));
    };
    const getActionBar = useCallback(() => (React.createElement(React.Fragment, null,
        React.createElement(Horizontal, { className: styles.actionHandlerWrapper },
            React.createElement(Vertical, { className: styles.gridSearch },
                React.createElement(SearchBar, { searchValue: "", searchPlaceholder: t(TranslationsKeys.SEARCH_PLACEHOLDER), onSearchChange: (searchTerm) => {
                        onSearchChange(searchTerm);
                    }, className: styles.searchBarContainer, "data-testid": "variable-search-bar" })),
            !selectedRowsIds.length && (React.createElement(Horizontal, { spacing: "d" }, renderActionsBtn(TranslationsKeys.CREATE_BUTTON, onClickCreateVariableBtn, IconID.PLUS)))),
        renderSelectionBar())), [
        selectedRowsIds,
        rowData,
        onClickEditVariableBtn,
        onClickRenameVariableBtn,
        onClickDeleteVariableBtn,
    ]);
    const onRowsSelected = (rowIds) => {
        onRowsSelectAction(rowIds, data, variableListInput, lastOperatedPage, setLastOperatedPage, selectedRowsIds, setSelectedRowsIds);
    };
    useEffect(() => {
        const filterredSelectedRowIds = getSelectedOnCurrentPage(selectedRowsIds, data);
        if (gridRef.current) {
            updateRowData(rowData, filterredSelectedRowIds, setRowData);
            refreshCells(gridRef, filterredSelectedRowIds, TableColumnHeaders.MORE_OPTION);
        }
    }, [selectedRowsIds]);
    useEffect(() => {
        if (data) {
            latestDataRef.current = data;
        }
        updateRowData(data, getSelectedOnCurrentPage(selectedRowsIds, data), setRowData);
        setIsLoading(false);
    }, [data, selectedRowsIds]);
    const onRowActionCB = ({ action, data: actionData }) => {
        currentData.current = actionData;
        if (action === ListActionType.EDIT) {
            openEditModal(actionData);
        }
        else if (action === ListActionType.DELETE) {
            setIsDeleteModalOpen(true);
        }
        else if (action === ListActionType.RENAME) {
            setIsRenameModalOpen(true);
        }
    };
    const onDeleteModalClose = useCallback(() => {
        setIsDeleteModalOpen(false);
    }, []);
    const onRenameModalClose = useCallback(() => {
        setIsRenameModalOpen(false);
        setRenameNameError(null);
    }, []);
    const onRenameConfirm = useCallback(async (newName) => {
        if (!currentData.current?.id) {
            return;
        }
        hideErrorBanner();
        setRenameNameError(null);
        try {
            await updateVariableName(currentData.current.id, newName);
            trackVariableRename(true);
            setIsRenameModalOpen(false);
            setRenameNameError(null);
            successBannerMode(MODE.RENAME);
            refetch();
        }
        catch (err) {
            const errorCode = extractErrorCode(err);
            trackVariableRename(false, errorCode);
            const debugMessage = extractDebugMessage(err);
            const isDuplicateNameError = errorCode === VARIABLE_NAME_EXISTS_ERROR_CODE ||
                (errorCode === COMMON_ERROR_CODE &&
                    debugMessage?.toLowerCase().includes('same name'));
            if (isDuplicateNameError) {
                const errorMsg = t(TranslationsKeys.ERROR_NAME_EXISTS);
                setRenameNameError(errorMsg);
            }
            else {
                const errorText = debugMessage || t(TranslationsKeys.ERROR_UPDATE);
                setErrorBannerMessage(errorText);
                setErrorBanner(true);
            }
        }
    }, [updateVariableName, refetch, t]);
    const onDeleteConfirm = async () => {
        hideErrorBanner();
        let ids = [currentData.current?.id || ''];
        if (selectedRowsIds.length > 0) {
            ids = [...selectedRowsIds];
        }
        const isSingleDelete = ids.length === 1;
        try {
            let result;
            if (isSingleDelete) {
                result = await deleteVariable(ids[0]);
            }
            else {
                result = await deleteVariables(ids);
            }
            if (result) {
                trackVariableDelete(true, {
                    variable_count: ids.length,
                    delete_mode: isSingleDelete ? 'single' : 'multi',
                });
                if (ids.length > 1) {
                    successBannerMode(MODE.DELETE_MULTI);
                }
                else {
                    successBannerMode(MODE.DELETE);
                }
                setSelectedRowsIds([]);
                onDeleteModalClose();
                const { offset, batchSize } = variableListInput;
                if (offset && ids.length === rowData.length) {
                    onChangeOffset(offset - batchSize);
                }
            }
            else {
                trackVariableDelete(false, {
                    variable_count: ids.length,
                    delete_mode: isSingleDelete ? 'single' : 'multi',
                });
                setErrorBannerMessage(t(TranslationsKeys.ERROR_DELETE));
                setErrorBanner(true);
                setSelectedRowsIds([]);
                onDeleteModalClose();
                setIsRefresh(true);
            }
        }
        catch (error) {
            trackVariableDelete(false, {
                variable_count: ids.length,
                delete_mode: isSingleDelete ? 'single' : 'multi',
                errorCode: extractErrorCode(error),
            });
            const { errorMessage, dependentsMessage } = categorizeDeleteError(error, isSingleDelete, t);
            if (dependentsMessage) {
                setDependentsInfo({
                    message: dependentsMessage,
                    variableName: currentData.current?.name || null,
                });
            }
            else {
                setDependentsInfo({ message: null, variableName: null });
            }
            setErrorBannerMessage(errorMessage);
            setErrorBanner(true);
            setSelectedRowsIds([]);
            onDeleteModalClose();
        }
    };
    const onSortingChanged = (sortState) => {
        const sortingColumn = sortState.find((el) => el.sort !== 'null') || sortState[0];
        const sortKey = sortingColumn &&
            sortKeyMap[sortingColumn.colId];
        const ascending = sortingColumn ? sortingColumn.sort === 'asc' : true;
        sortData.current.sort = sortKey;
        sortData.current.sortAscending = ascending;
        setVariableListInput({
            ...variableListInput,
            sort: sortKey,
            sortAscending: ascending,
        });
    };
    const setAgGridApi = (agGridApi) => {
        gridRef.current = agGridApi;
    };
    const suppressNavigation = ({ colDef, }) => colDef.colId === TableColumnHeaders.MORE_OPTION;
    const listViewProps = {
        data: rowData,
        columns: getColumnDefinitions(),
        options: {
            suppressMovable: true,
            hasCheckboxes: true,
            onRowsSelected,
            selectedRowsIds,
            checkboxHeaderClass: [styles.headerStyle],
            customWidth: 48,
        },
        agGridContext: {
            onRowAction: onRowActionCB,
        },
        onSortingChanged,
        loading,
        setAgGridApi,
        suppressKeyboardEvent: suppressNavigation,
    };
    return (React.createElement(Vertical, null,
        errorBanner && (React.createElement(Alert.Banner, { message: errorBannerMessage, isVisible: true, type: BannerAlertType.ERROR, onClose: hideErrorBanner, className: styles.errorBanner, primaryAction: dependentsInfo.message
                ? {
                    label: t(TranslationsKeys.VIEW_DEPENDENTS_BUTTON),
                    onClick: () => setShowDependentsModal(true),
                }
                : undefined })),
        React.createElement(Vertical, { "data-testid": "variables-page-component", className: styles.container },
            React.createElement(Vertical, { className: styles.headerWrapper, spacing: "g" },
                React.createElement(Vertical, { "data-testid": "variables-page-header", spacing: "b" },
                    React.createElement(Typography, { variant: "page-title", noMargin: true }, t(TranslationsKeys.PAGE_TITLE)),
                    React.createElement(Typography, { variant: "p", noMargin: true, className: styles.pageDescription }, t(TranslationsKeys.PAGE_DESCRIPTION)))),
            React.createElement(Horizontal, { column: true, className: styles.contentHeight },
                React.createElement(Vertical, null,
                    React.createElement(LoadingIndicator.Global, { hideLoader: !isLoading },
                        React.createElement(Vertical, null,
                            getActionBar(),
                            React.createElement(ListContainer, { testId: "variables-list-page", data: data || [], listViewProps: listViewProps, noDataProps: noDataProps, offSet: variableListInput.offset, paginationSize: variableListInput.batchSize, isLastBatch: isLastBatch, onChangeOffset: onChangeOffset, headerHeight: 32, isLoading: isLoading, onKeyDown: (e) => {
                                    const focusedCell = gridRef.current?.getFocusedCell();
                                    if (e.key === 'Enter' &&
                                        gridRef.current
                                            ?.getFocusedCell()
                                            ?.column?.getColId() ===
                                            TableColumnHeaders.NAME) {
                                        const rowNode = gridRef.current.getDisplayedRowAtIndex(focusedCell.rowIndex);
                                        if (rowNode) {
                                            openEditModal(rowNode.data);
                                        }
                                    }
                                } }, metadataList))),
                    isModalOpened && (React.createElement(CreateEditModal, { isModalOpened: isModalOpened, closeModal: () => {
                            setIsModalOpened(false);
                        }, isEditMode: isEditMode, variableId: currentData.current?.id, variableData: currentData.current || undefined, setIsLoading: setIsLoading, successBannerMode: successBannerMode, setErrorBanner: setErrorBanner, setErrorBannerMessage: setErrorBannerMessage })),
                    isDeleteModalOpen && (React.createElement(DeleteConfirmationModal, { name: currentData.current?.name || '', isModalOpen: isDeleteModalOpen, onDeleteConfirm: onDeleteConfirm, onDeleteModalClose: onDeleteModalClose, isMultiSelected: selectedRowsIds.length > 0, selectedCount: selectedRowsIds.length })),
                    isRenameModalOpen && (React.createElement(RenameModal, { currentName: currentData.current?.name || '', isModalOpen: isRenameModalOpen, onRenameConfirm: onRenameConfirm, onRenameModalClose: onRenameModalClose, externalNameError: renameNameError, clearExternalNameError: () => setRenameNameError(null) })),
                    showDependentsModal && dependentsInfo.message && (React.createElement(DependentsModal, { isOpen: showDependentsModal, onClose: () => setShowDependentsModal(false), dependents: dependentsInfo.message, variableName: dependentsInfo.variableName || '' })),
                    React.createElement(GlobalAlert.Toast, { message: getToastMessage(mode, selectedRowsIds.length || 1), isVisible: isToastAlertVisible, onClose: () => {
                            setIsToastAlertVisible(false);
                        }, autoHide: true }))))));
};
export const Variables = () => React.createElement(VariablesContainer, null);
//# sourceMappingURL=variables.container.js.map